import axios from '../services/axios';
import { dadosDaConta, ehDemonstracao } from './planoConta';

/**
 * Chat do comercial (Tawk.to), para contas em demonstração.
 *
 * Essas contas falam com os setores que atendem por ali — comercial, pré-venda
 * —, não com o suporte técnico, que usa o TomTicket.
 *
 * O balão carrega junto com a sessão, minimizado, e permanece na tela: é o que
 * dá aos triggers configurados no painel do Tawk um lugar para aparecer, e
 * alcança também quem nunca clicou no menu. Clicar em "Chat ao vivo" apenas
 * abre a janela.
 */

const ID_TAG = 'tawk-chat-script';

/** Abaixo disto o balão cobre a tela; era a regra do bloco antigo no index.html. */
const LARGURA_MINIMA = 768;

const SRC = 'https://embed.tawk.to/6ac684c81d4a0934c20d7234/1k4bndcde';

/** Sala de atendimento em nova aba — alternativa ao balão, hoje sem uso. */
const LINK_EXTERNO = 'https://tawk.to/espiaonfeteste';

/** Prefixos das chaves que o Tawk deixa no navegador. */
const PREFIXOS_DA_CONVERSA = ['twk_', 'TawkConnectionTime', 'Tawk_'];

const api = () => (typeof window !== 'undefined' ? window.Tawk_API : undefined);

/** O widget só expõe estes métodos depois de carregar. */
const widgetPronto = () => Boolean(api()?.maximize);

const ehDesktop = () => typeof window === 'undefined' || window.innerWidth >= LARGURA_MINIMA;

/**
 * Última visibilidade pedida, aplicada assim que o widget terminar de carregar.
 *
 * A API só existe depois que o script sobe. Sem isto, mudar de tela durante a
 * carga deixaria o balão visível numa rota pública, porque hideWidget ainda não
 * estaria disponível.
 */
let visivelDesejado = true;

const aplicarVisibilidade = () => {
  const tawk = api();
  if (!tawk) return;

  try {
    if (visivelDesejado) tawk.showWidget?.();
    else tawk.hideWidget?.();
  } catch {
    /* ainda cedo; o onLoad chama de novo */
  }
};

/** Informa se o widget já está na página. */
export const tawkCarregado = () => Boolean(document.getElementById(ID_TAG));

/** Esconde o balão sem descarregar o script. Usado nas telas públicas. */
export const ocultarTawk = () => {
  visivelDesejado = false;
  aplicarVisibilidade();
};

/** Volta a exibir o balão escondido por ocultarTawk. */
export const mostrarTawk = () => {
  visivelDesejado = true;
  aplicarVisibilidade();
};

/**
 * Identidade assinada pelo backend, guardada para o setAttributes.
 *
 * O hash prova ao Tawk que nome e e-mail vieram de nós. Sem ele, com o modo
 * seguro ligado no painel, os atributos são recusados — e sem o modo seguro
 * qualquer pessoa poderia forjá-los pelo console.
 */
let identidade = null;

/**
 * Busca a identidade assinada. Falha em silêncio: o chat abre de qualquer
 * forma, apenas sem identificar quem está falando.
 */
const buscarIdentidade = async () => {
  if (identidade) return identidade;

  try {
    const res = await axios.post('/api/tickets/', { dest: 'tawk' });
    const { nome, email, hash, telefone } = res?.data || {};

    if (hash && email) identidade = { nome, email, hash, telefone };
  } catch {
    /* segue sem assinatura */
  }

  return identidade;
};

/**
 * Diz ao Tawk quem está do outro lado.
 *
 * Sem isto o atendente recebe um visitante anônimo e precisa perguntar nome,
 * empresa e e-mail antes de ajudar. Os dados saem da sessão já aberta.
 *
 * As chaves aceitam apenas letras, números e hífen — é a regra da API deles.
 */
const identificarVisitante = () => {
  const tawk = api();
  if (!tawk?.setAttributes) return;

  const conta = dadosDaConta();

  const atributos = {};

  // Nome e e-mail vão com o hash do backend; sem ele, o modo seguro do Tawk
  // recusaria o par. Os demais atributos não exigem assinatura.
  if (identidade?.hash) {
    atributos.name = identidade.nome || conta.nome;
    atributos.email = identidade.email;
    atributos.hash = identidade.hash;

    // Só vai quando o backend conseguiu normalizar para E.164, que é o formato
    // exigido pelo Tawk. Número de enchimento no cadastro é descartado lá.
    if (identidade.telefone) atributos.phone = identidade.telefone;
  } else {
    if (conta.nome) atributos.name = conta.nome;
    if (conta.email) atributos.email = conta.email;
  }

  if (conta.empresa) atributos.empresa = conta.empresa;
  if (conta.cnpj) atributos.cnpj = conta.cnpj;
  if (conta.conta) atributos['conta-id'] = conta.conta;
  if (conta.usuario) atributos['usuario-id'] = conta.usuario;
  if (conta.plano) atributos.plano = conta.plano;

  if (Object.keys(atributos).length === 0) return;

  try {
    // O callback recebe erro de validação; não vale interromper o atendimento
    // por causa disso, então apenas registramos.
    tawk.setAttributes(atributos, (erro) => {
      if (erro) console.warn('Tawk: não foi possível identificar o visitante', erro);
    });
  } catch {
    /* API ainda não pronta: a próxima abertura tenta de novo */
  }
};

/**
 * Injeta o script do widget.
 *
 * @param {boolean} abrir abre a janela assim que o widget ficar pronto; sem
 *   isto o balão nasce minimizado, que é como ele deve aparecer ao entrar no
 *   sistema.
 */
const carregar = (abrir) => {
  if (tawkCarregado()) return true;

  try {
    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();
    visivelDesejado = true;

    window.Tawk_API.onLoad = () => {
      // O widget redefine seu objeto ao inicializar, então tudo o que depende
      // da API vai aqui, quando ela existe de fato.
      identificarVisitante();
      aplicarVisibilidade();

      if (abrir) {
        try {
          window.Tawk_API.maximize?.();
        } catch {
          /* nada a fazer */
        }
      }
    };

    const tag = document.createElement('script');
    tag.id = ID_TAG;
    tag.async = true;
    tag.src = SRC;
    tag.charset = 'UTF-8';
    tag.setAttribute('crossorigin', '*');

    document.body.appendChild(tag);
    return true;
  } catch {
    return false;
  }
};

/**
 * Carrega o balão, minimizado. Chamado ao entrar no sistema.
 *
 * @return {boolean} false quando a conta não é de demonstração.
 */
export const carregarTawk = async () => {
  if (!ehDemonstracao() || !ehDesktop()) return false;

  if (tawkCarregado()) {
    mostrarTawk();
    return true;
  }

  // Antes de injetar: assim a identidade já está pronta quando o onLoad
  // disparar, e o visitante é identificado na primeira tentativa.
  await buscarIdentidade();

  return carregar(false);
};

/**
 * Abre a sala de atendimento em uma nova aba.
 *
 * Preservada: o atendimento usa o balão embutido (abrirTawk). Para trocar, é só
 * o menu de ajuda chamar esta função no lugar da outra.
 *
 * @return {boolean} false quando a conta não é de demonstração.
 */
export const abrirChatComercial = () => {
  if (!ehDemonstracao()) return false;

  try {
    window.open(LINK_EXTERNO, '_blank', 'noopener,noreferrer');
    return true;
  } catch {
    return false;
  }
};

/**
 * Abre o chat no widget embutido, dentro da própria página.
 *
 * É o caminho em uso pelo item "Chat ao vivo" nas contas de demonstração.
 *
 * @return {boolean} false quando a conta não é de demonstração.
 */
export const abrirTawk = async () => {
  if (!ehDemonstracao() || !ehDesktop()) return false;

  await buscarIdentidade();

  if (widgetPronto()) {
    try {
      api().showWidget?.();
      api().maximize?.();
      identificarVisitante();
      return true;
    } catch {
      /* segue para a carga abaixo */
    }
  }

  return carregar(true);
};

/**
 * Encerra o chat e apaga o rastro da conversa. Chamado ao sair do sistema.
 *
 * O Tawk guarda a conversa no navegador, como o TomTicket: sem esta limpeza,
 * quem entrasse em seguida no mesmo computador abriria o chat e veria a
 * conversa do usuário anterior.
 *
 * @return {boolean} true quando havia widget carregado. Quem chama usa isso
 *   para decidir recarregar a página: o script do Tawk roda uma vez só e não
 *   reinicializa se for reinjetado, então um segundo login na mesma página
 *   ficaria sem chat.
 */
export const encerrarTawk = () => {
  identidade = null;

  const haviaWidget = Boolean(document.getElementById(ID_TAG)) || Boolean(api());

  try {
    api()?.endChat?.();
  } catch {
    /* sem sessão ativa */
  }

  // Esconder pelo próprio widget antes de remover: ele sabe desmontar o que
  // montou, e o que sobrar some com os seletores abaixo.
  try {
    api()?.hideWidget?.();
  } catch {
    /* sem API: segue para a remoção */
  }

  // As chaves do Tawk variam com a propriedade e a visita, então a limpeza
  // varre por prefixo em vez de listar nomes.
  [window.localStorage, window.sessionStorage].forEach((armazem) => {
    try {
      Object.keys(armazem)
        .filter((chave) => PREFIXOS_DA_CONVERSA.some((pref) => chave.startsWith(pref)))
        .forEach((chave) => armazem.removeItem(chave));
    } catch {
      /* armazenamento bloqueado */
    }
  });

  try {
    document.cookie.split(';').forEach((bruto) => {
      const chave = bruto.split('=')[0].trim();
      if (PREFIXOS_DA_CONVERSA.some((pref) => chave.startsWith(pref))) {
        document.cookie = `${chave}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      }
    });
  } catch {
    /* idem */
  }

  // O Tawk monta bem mais que a tag do script — iframes e contêineres com
  // prefixo próprio. Remover só a tag deixava o balão na tela.
  const SELETORES = [
    `#${ID_TAG}`,
    'iframe[src*="tawk.to"]',
    '[id^="tawkchat"]',
    '[id^="tawk-"]',
    'div[class*="tawk-"]',
  ];

  try {
    document.querySelectorAll(SELETORES.join(', ')).forEach((el) => el.remove());
  } catch {
    /* elemento já removido */
  }

  try {
    delete window.Tawk_API;
    delete window.Tawk_LoadStart;
  } catch {
    window.Tawk_API = undefined;
  }

  return haviaWidget;
};

export default abrirTawk;
